import React from "react";
import { cn } from "@/lib/utils";
import InquiryForm from "./inquiry-form";
import TextCardToggle from "@/components/ui/text-card-toggle";

export interface FormInquiryPromotionProps {
  id?: string;
  className?: string;
  title?: string;
  description?: string | React.ReactNode;
  additionalData?: { title?: string; description?: string | React.ReactNode }[];
  color?: "light-orange" | "white";
}

const FormInquiryPromotion: React.FC<FormInquiryPromotionProps> = ({
  id = "form-inquiry-promotion",
  className,
  title = "Our Care Services: Human-Focused, Expertly Delivered",
  description = (
    <div className="flex flex-col gap-2">
      <p>
        Watching a loved one struggle with their health is heart-wrenching. The
        late-night worries, the overwhelming decisions, and the constant
        question—&quot;Am I doing enough?&quot;—can take an emotional toll. You
        want them to be safe, comfortable, and surrounded by people who truly
        care—but finding the right support isn&apos;t easy.
      </p>
      <p>
        That&apos;s why we&apos;re here. Whether it&apos;s long-term care,
        private duty medical support, specialized care, or assistance for
        specific health conditions, our dedicated team of caregivers, nurses,
        and therapists provides more than just care—we provide relief,
        reassurance, and a compassionate presence when you need it most.
      </p>
      <p>
        We know that choosing care isn&apos;t just a practical
        decision—it&apos;s a deeply personal one. That&apos;s why we create
        individualized care plans that prioritize dignity, independence, and
        emotional well-being, all in the comfort of home. While we focus on
        their health and safety, you can focus on being family again—sharing
        stories, laughter, and moments that truly matter.
      </p>
      <p>
        Let us walk this journey with you, offering the support, expertise, and
        peace of mind you and your loved one deserve.
      </p>
    </div>
  ),
  additionalData,
  color = "light-orange",
}) => {
  return (
    <section
      id={id}
      aria-label={id}
      className={cn(
        color === "light-orange" ? "bg-primary-100" : "bg-white",
        className
      )}
    >
      <div className="mx-auto flex w-full max-w-lg flex-col gap-4 p-4 md:flex-col md:gap-10 md:px-6 md:py-8 lg:flex-row lg:gap-6 lg:px-3 lg:py-14">
        <div className="flex flex-col gap-8 lg:w-[616px]">
          <TextCardToggle
            variant="mobile"
            className="flex w-full lg:w-[616px]"
            containerClassName="gap-2 md:gap-3 lg:gap-4"
            titleClassName="font-montserrat !text-secondary-500 text-heading-4 md:text-heading-3 lg:text-heading-2"
            title={title}
            contentClassName="md:!h-fit"
            defaultCollapsed={true}
            collapsedHeight={144}
            toggleClassName="md:hidden"
          >
            {typeof description === "string" ? (
              <div
                className="rich-text [&_a]:hover:text-primary-700 [&_a]:focus:text-primary-700 [&_a]:rounded-md [&_a]:font-roboto [&_a]:text-body-1-400 [&_a]:text-primary-600 [&_a]:underline [&_a]:underline-offset-2 [&_a]:hover:no-underline [&_a]:focus:no-underline [&_a]:focus:outline [&_a]:focus:outline-1 [&_a]:focus:outline-offset-2 [&_a]:focus:outline-black"
                dangerouslySetInnerHTML={{ __html: description || "" }}
              />
            ) : (
              description
            )}
          </TextCardToggle>

          {additionalData &&
            additionalData.length > 0 &&
            additionalData.map(({ title, description }, index) => (
              <TextCardToggle
                key={`additional-data-${index}`}
                variant="mobile"
                className="flex w-full lg:w-[616px]"
                containerClassName="gap-2 md:gap-3 lg:gap-4"
                titleClassName="font-montserrat !text-secondary-500 text-heading-4 md:text-heading-3 lg:text-heading-2"
                title={title}
                contentClassName="md:!h-fit"
                defaultCollapsed={true}
                collapsedHeight={144}
                toggleClassName="md:hidden"
              >
                {typeof description === "string" ? (
                  <div
                    className="rich-text"
                    dangerouslySetInnerHTML={{ __html: description || "" }}
                  />
                ) : (
                  description
                )}
              </TextCardToggle>
            ))}
        </div>
        <div className="w-full lg:w-[616px]">
          <InquiryForm className="shadow-[2px_4px_16px_2px_rgba(82,77,75,0.12)] md:shadow-[4px_6px_20px_2px_rgba(82,77,75,0.12)] lg:shadow-[4px_8px_24px_2px_rgba(82,77,75,0.1)]" />
        </div>
      </div>
    </section>
  );
};

export default FormInquiryPromotion;
